import React, { useState, useContext, useEffect } from 'react';
import { Form, Row, Col, Button, Card } from 'react-bootstrap';
import { CheckCircle, Hotel, BedDouble, MapPin, Calendar, Users, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppContext from '../../../../context/AppContext';
import "../../../../assets/css/customer/publicCustomerCss/BookingComponent/pricingPayment.css";

const PricingPayment = ({
  data,
  errors,
  handleChange,
  hotel,
  room,
  nights,
  totalAmount,
  pricePerNight,
  prevStep,
  handleSubmit,
  loading,
}) => {
const navigate = useNavigate();
const { baseUrl, authToken } = useContext(AppContext);
const [commissionRate, setCommissionRate] = useState(null);

  useEffect(() => {
    axios
      .get(`${baseUrl}/customer/commission-rate`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => setCommissionRate(res.data?.commission_rate ?? 0))
      .catch(() => setCommissionRate(0));
  }, []);

  const rate = commissionRate ?? 0;
  const reservedAmount  = +(totalAmount * (rate / 100)).toFixed(2);
  const remainingAmount = +(totalAmount - reservedAmount).toFixed(2);

  return (
    <div className="hb-pricing-payment-wrapper">

      <Card className="hb-final-summary-card mb-3">
        <Card.Body>
          <Card.Title className="hb-summary-title mb-3">🏨 Booking Summary</Card.Title>

          <div className="hb-summary-hotel-row mb-2">
            <Hotel size={18} className="me-2 text-primary" />
            <strong>{hotel?.name}</strong>
            <span className="ms-2 text-muted">
              <MapPin size={14} className="me-1" />{hotel?.location}
            </span>
          </div>

          <div className="hb-summary-room-row mb-3">
            <BedDouble size={18} className="me-2 text-secondary" />
            <span>
              {room?.name} &nbsp;|&nbsp; Room # {room?.number} &nbsp;|&nbsp; Floor: {room?.floor}
            </span>
          </div>

          <hr className="my-2" />

          <Row className="mb-2">
            <Col md={6}>
              {[
                ['Guest Name',   data.guestName],
                ['Phone',        data.guestPhone],
                ['City',         data.city],
              ].map(([label, value]) => (
                <div key={label} className="hb-summary-item">
                  <span className="hb-summary-label">{label}:</span>
                  <span className="hb-summary-value">{value || '—'}</span>
                </div>
              ))}
            </Col>
            <Col md={6}>
              {[
                ['Check-in',  data.checkIn],
                ['Check-out', data.checkOut],
                ['Guests',    `${data.numberOfGuests} Person(s)`],
                ['Nights',    `${nights} Night(s)`],
              ].map(([label, value]) => (
                <div key={label} className="hb-summary-item">
                  <span className="hb-summary-label">{label}:</span>
                  <span className="hb-summary-value">{value || '—'}</span>
                </div>
              ))}
            </Col>
          </Row>

          <hr className="my-2" />

          <div className="hb-price-breakdown mt-2">
            <div className="hb-breakdown-row">
              <span>Room Rate ({nights} night{nights > 1 ? 's' : ''} × Rs. {pricePerNight?.toLocaleString()})</span>
              <span>Rs. {totalAmount.toLocaleString()}</span>
            </div>

           <div className="hb-breakdown-row hb-breakdown-row--highlight">
              <span>
                Reservation Amount
                <span className="hb-bd-badge">Pay Now · {rate}%</span>
              </span>
              <span className="hb-bd-value--accent">Rs. {reservedAmount.toLocaleString()}</span>
            </div>

            <div className="hb-breakdown-row">
              <span>
                Remaining Balance
                <span className="hb-bd-badge hb-bd-badge--muted">Pay Later · {(100 - rate)}%</span>
              </span>
              <span className="hb-bd-value--muted">Rs. {remainingAmount.toLocaleString()}</span>
            </div>
            <div className="hb-breakdown-note">
              <ShieldCheck size={14} className="me-1" />
              <span>
                You are only charged <strong>Rs. {reservedAmount.toLocaleString()}</strong> today.
                The remaining balance is due before check-in.
              </span>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Form>
        <Card className="hb-terms-card mb-4">
          <Card.Body>
            <Form.Check
              type="checkbox"
              name="agreeTerms"
              checked={data.agreeTerms}
              onChange={handleChange}
              isInvalid={!!errors.agreeTerms}
              className="hb-terms-checkbox"
              label={
                <span>
I agree to the{' '}
<span
    className="hb-terms-link"
    onClick={() =>
        navigate('/terms-and-conditions', {
            state: {
                returnTo: '/customer-booking',
                returnState: {
                    hotel,
                    room,
                    checkInDate: data.checkIn,
                    checkOutDate: data.checkOut,
                    nights,
                    totalPrice: totalAmount,
                    resumeStep: 2,
                    formData: data,
                },
            },
        })
    }
>
    Terms & Conditions
</span> and{' '}
<span
    className="hb-terms-link"
    onClick={() =>
        navigate('/privacy-policy', {
            state: {
                returnTo: '/customer-booking',
                returnState: {
                    hotel,
                    room,
                    checkInDate: data.checkIn,
                    checkOutDate: data.checkOut,
                    nights,
                    totalPrice: totalAmount,
                    resumeStep: 2,
                    formData: data,
                },
            },
        })
    }
>
    Privacy Policy
</span> *                 
                </span>
              }
            />
            {errors.agreeTerms && (
              <div className="text-danger mt-1" style={{ fontSize: '0.8rem' }}>
                {errors.agreeTerms}
              </div>
            )}
          </Card.Body>
        </Card>

        <div className="hb-booking-navigation">
          <Button
            variant="outline-secondary"
            onClick={prevStep}
            className="hb-btn-booking-back"
            disabled={loading}
          >
            <i className="fas fa-arrow-left me-2"></i> Back
          </Button>

          <Button
            onClick={handleSubmit}
            disabled={!data.agreeTerms || loading}
            className="hb-btn-booking-confirm"
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                Booking...
              </>
            ) : (
              <>
               <CheckCircle size={18} className="me-2" /> Confirm & Pay  </>
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default PricingPayment;